// ─── src/components/nav/HomeNav.jsx ──────────────────────────────────────────
import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { supabase } from "../../lib/supabaseClient";
import { useTheme } from "../../theme/ThemeContext";
import { buildTree } from "../../pages/AdminModules/menu/menuUtils";
import MegaMenuPanel from "./MegaMenuPanel";
import MobileDrawerAccordion from "./MobileDrawerAccordion";
import { useAdaptiveColor } from "../../hooks/useAdaptiveColor";
import { navigateTo } from "../../utils/navigate";
import { imgUrl } from "../../utils/imgUrl";

// ── Site branding defaults ────────────────────────────────────────────────────
// New identity (matches the Edit masthead family): LWD as the centred
// wordmark with the menu directly beneath it. Reads as a publication
// masthead, not a marketplace header, same generation of design across
// /the-edit and the rest of the site.
// Matches the post-fetch override in the site_branding load effect below —
// kept in sync deliberately, so the very first render (before that fetch
// resolves) already shows the locked serif logo instead of flashing the
// plain "LWD" text placeholder first. If the two ever drift, the flash
// this was fixed to remove comes back.
const DEFAULT_BRANDING = {
  logo_type:                 "image",
  logo_text:                 "LWD",
  logo_font:                 "serif",
  logo_color:                null,
  logo_image_light:          'https://qpkggfibwreznussudfh.supabase.co/storage/v1/object/public/brand-assets/logo/luxury-wedding-directory-serif-black.png',
  logo_image_dark:           'https://qpkggfibwreznussudfh.supabase.co/storage/v1/object/public/brand-assets/logo/luxury-wedding-directory-serif-white.png',
  logo_image_mobile:         null,
  logo_alt_text:             "Luxury Wedding Directory",
  logo_link_target:          "/",
  header_layout:             "logo-center-stacked",
  show_logo_in_header:       true,
  header_logo_width_desktop: 232,
  header_logo_width_mobile:  155,
  header_logo_rendering:     "contain",
  transparent_bg_expected:   true,
  logo_align_header:         "center",
  menu_align_header:         "center",
};

const GD = "var(--font-heading-primary)";
const NU = "var(--font-body)";

// Maps nav_action values to handler functions passed as props
// Every in-app target routes through navigateTo(), which pushes state instead of
// reloading the document. External urls and open_new_tab still get real
// navigations, see src/utils/navigate.js.
function resolveHandler(item, handlers) {
  const { nav_action, url, slug, open_new_tab } = item;
  if (nav_action === "about")          return () => handlers.onNavigateAbout?.();
  if (nav_action === "planning")       return () => handlers.onNavigateStandard?.();
  if (nav_action === "aura-discovery") return () => navigateTo("/discovery/aura");
  if (nav_action === "browse" && !url)  return () => navigateTo("/venue");
  if (nav_action === "the-edit")       return () => navigateTo("/lwd-edit");
  if (nav_action === "publications")   return () => navigateTo("/publications");
  if (nav_action === "mag_category")   return () => { url && navigateTo(url); };
  if (nav_action === "join")                return () => navigateTo("/join");
  if (nav_action === "apply-for-inclusion") return () => navigateTo("/apply-for-inclusion");
  // Legacy alias kept for ONE migration window so live nav_items rows still
  // route correctly until the admin UI is used to flip nav_action values.
  // After the next admin sweep, this line can be deleted.
  if (nav_action === "list-your-business") return () => navigateTo("/apply-for-inclusion");
  if (nav_action === "h2")                 return () => navigateTo("/h2");
  if (nav_action === "contact")            return () => navigateTo("/contact");
  if (nav_action === "press")              return () => navigateTo("/press");
  if (nav_action === "artistry-awards")    return () => navigateTo("/artistry-awards");
  if (nav_action === "all-destinations")   return () => navigateTo("/all-destinations");
  if (url) return () => navigateTo(url, { newTab: open_new_tab });
  if (slug) return () => navigateTo(`/${slug}`);
  return null;
}

// ── Vendor Mega Menu, department structure ───────────────────────────────────
// Five Harrods-style departments. Each links to its category route.
// Injected into nav items at load time, replacing any existing vendor dropdown.
// IMPORTANT: declared before FALLBACK_LINKS so the spread { ...VENDOR_MEGA_ITEM }
// doesn't hit the temporal dead zone.
const VENDOR_SECTIONS = [
  {
    label: "The Celebration",
    items: [
      { id: "vs-venues",     label: "Wedding Venues",    url: "/wedding-venues" },
      { id: "vs-planners",   label: "Wedding Planners",  url: "/wedding-planners" },
      { id: "vs-celebrants", label: "Celebrants",        url: "/celebrants" },
      { id: "vs-catering",   label: "Catering",          url: "/caterers" },
      { id: "vs-cakes",      label: "Cakes",             url: "/wedding-cakes" },
      { id: "vs-entertain",  label: "Entertainment",     url: "/entertainment" },
    ],
  },
  {
    label: "The Creative Team",
    items: [
      { id: "vs-photo",    label: "Photographers",     url: "/photographers" },
      { id: "vs-video",    label: "Videographers",     url: "/videographers" },
      { id: "vs-florists", label: "Florists",          url: "/florists" },
      { id: "vs-decor",    label: "Decor and Styling", url: "/event-design" },
    ],
  },
  {
    label: "Fashion and Beauty",
    items: [
      { id: "vs-bridal",     label: "Bridal Fashion",  url: "/bridal-boutiques" },
      { id: "vs-groom",      label: "Groomswear",      url: "/guest-attire" },
      { id: "vs-hair",       label: "Hair and Makeup", url: "/hair-makeup" },
      { id: "vs-jewellery",  label: "Jewellery",       url: "/jewellers" },
    ],
  },
  {
    label: "Details and Production",
    items: [
      { id: "vs-transport",  label: "Transport",          url: "/transport" },
      { id: "vs-stationery", label: "Stationery",         url: "/stationery" },
      { id: "vs-marquees",   label: "Marquees",           url: "/wedding-vendors" },
    ],
  },
  {
    label: "Travel and Aftercare",
    items: [
      { id: "vs-honeymoons", label: "Honeymoons",     url: "/wedding-vendors" },
      { id: "vs-travel",     label: "Luxury Travel",  url: "/wedding-vendors" },
    ],
  },
];

const VENDOR_MEGA_ITEM = {
  id:                  "nav-vendors-mega",
  label:               "Vendors",
  type:                "mega_menu",
  mega_menu_source:    "manual",
  sections:            VENDOR_SECTIONS,
  has_cta_in_panel:    true,
  panel_cta_label:     "Explore the Collection",
  panel_cta_link:      "/wedding-vendors",
  show_descriptions:   false,
  visible:             true,
};

// Fallback links used if Supabase is unavailable. "Real Weddings" was
// removed from the menu, the page is hidden until it's rebuilt to the
// editorial standard. The route is also redirected in main.jsx so a
// direct URL hit lands on home rather than the legacy SaaS layout.
//
// Position 0: "H", single-letter masthead ornament at the head of the
// nav cluster. Functionally returns home when clicked, but reads as a
// typographic mark, not as a "Home" link (the wordmark also returns
// home, by long-standing luxury-publication convention).
const FALLBACK_LINKS = [
  { id: "f0",  label: "H",              nav_action: "home",           url: "/",      visible: true },
  { ...VENDOR_MEGA_ITEM, id: "f1" },
  { id: "f2",  label: "Aura Discovery", nav_action: "aura-discovery",                visible: true },
  { id: "f4",  label: "Planning",       nav_action: "planning",                       visible: true },
  { id: "f5",  label: "About",          nav_action: "about",                          visible: true },
  { id: "f9",  label: "Press",          nav_action: "press",          url: "/press", visible: true },
  { id: "f6",  label: "LWD Edit",       nav_action: "the-edit",                       visible: true },
  { id: "f8",  label: "H2",             nav_action: "h2",                             visible: true },
  { id: "f7",  label: "Apply for Inclusion", nav_action: "apply-for-inclusion", visible: true, type: "cta", cta_style: "editorial" },
];

// Translate any legacy "List Your …" CTA labels arriving from the nav_items
// table into the canonical "Apply for Inclusion" wording AND force its
// destination to /apply-for-inclusion. This way the CTA always reads + links
// correctly even if the live DB row has stale label, url, slug or
// nav_action, no admin-UI edit required.
function translateLegacyCta(item) {
  if (item?.type === 'cta' && /list[\s_-]*your[\s_-]*(venue|business|property)/i.test(item.label || '')) {
    return {
      ...item,
      label:      'Apply for Inclusion',
      cta_style:  'editorial',
      nav_action: 'apply-for-inclusion',
      url:        '',           // clear any DB url so nav_action wins in resolveHandler
      slug:       '',
    };
  }
  return item;
}

// "Home" written out is the wordmark click target, surfacing it as a
// nav item with the literal label "Home" lowers the tone. We DO allow a
// single-letter "H" (or any short masthead ornament under three chars)
// because that's an intentional editorial gesture, not a redundant link.
// Anything that fully spells out "Home" gets stripped before render so
// the menu doesn't read like a SaaS site map.
function isHomeLink(item) {
  if (!item) return false;
  if (typeof item.label === 'string' && /^\s*home\s*$/i.test(item.label)) return true;
  return false;
}

// Hide pages that have been removed from the public navigation. The page
// itself may still exist in the codebase but should not be discoverable
// via the menu. DB nav_items entries pointing to these slugs / actions
// are filtered out so no admin-UI edit is required.
//
// Currently hidden:
//   • Real Weddings, pending editorial rebuild
function isHiddenLink(item) {
  if (!item) return true;
  if (item.nav_action === 'real-weddings') return true;
  if (typeof item.label === 'string' && /^\s*real\s+weddings\s*$/i.test(item.label)) return true;
  if (typeof item.url === 'string' && /^\/real-weddings(\/|$|\?)/.test(item.url)) return true;
  return false;
}

// Finds any "vendor/browse" nav item in the DB tree and upgrades it to the
// vendor mega menu. If nothing matches, inserts the mega item at position 2.
function injectVendorMega(items) {
  const idx = items.findIndex(i =>
    /vendor/i.test(i.label || "") ||
    i.nav_action === "vendors" ||
    i.nav_action === "wedding-vendors" ||
    i.nav_action === "browse"
  );
  if (idx >= 0) {
    return items.map((item, i) =>
      i === idx ? { ...VENDOR_MEGA_ITEM, id: item.id } : item
    );
  }
  // Not found in DB, inject after the first nav item (the H ornament)
  const insertAt = Math.min(1, items.length);
  return [
    ...items.slice(0, insertAt),
    VENDOR_MEGA_ITEM,
    ...items.slice(insertAt),
  ];
}

// ── LwdMasthead, the publication-grade wordmark lockup ─────────────────────
// Classic publication masthead vocabulary (Vogue / Harper's / Aman): hairline
// gold rules flanking the wordmark with tiny diamond ornaments, the wordmark
// in serif caps, and a wide-tracked descriptor underneath. The whole lockup
// fades up + the rules draw outward on first paint so loading the page feels
// like a magazine cover unfolding rather than text appearing.
function LwdMasthead({ brandColor, gold, fontSerif, fontUtility, isMobile }) {
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setLoaded(true));
    return () => cancelAnimationFrame(id);
  }, []);

  // Mobile rules: shorter, softer, support the logo rather than compete.
  // Desktop rules: full editorial spread.
  const ruleClamp = isMobile ? 'clamp(20px, 5vw, 52px)' : 'clamp(40px, 8vw, 110px)';
  // Slightly dimmer gold on mobile so lines recede behind the wordmark
  const ruleAlpha = isMobile ? '77' : 'aa';

  const ruleStyle = (side) => ({
    flex: '0 0 auto',
    height: 1,
    maxWidth: loaded ? ruleClamp : 0,
    width:    ruleClamp,
    background: side === 'left'
      ? `linear-gradient(to right, transparent 0%, ${gold}${ruleAlpha} 60%, ${gold} 100%)`
      : `linear-gradient(to left,  transparent 0%, ${gold}${ruleAlpha} 60%, ${gold} 100%)`,
    transition: 'max-width 0.9s cubic-bezier(0.22, 0.61, 0.36, 1) 0.15s',
  });

  const ornament = (
    <span aria-hidden style={{
      width: 4, height: 4,
      background: gold,
      transform: 'rotate(45deg)',
      flex: '0 0 auto',
      opacity: loaded ? (isMobile ? 0.7 : 1) : 0,
      transition: 'opacity 0.5s ease 0.45s',
    }} />
  );

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      // More breathing room between LWD and tagline on mobile
      gap: isMobile ? 7 : 3,
      padding: '4px 0 3px',
    }}>
      {/* Rule · ornament · LWD · ornament · Rule */}
      <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 'clamp(8px, 1.2vw, 16px)' : 'clamp(12px, 1.6vw, 22px)' }}>
        <span style={ruleStyle('left')} />
        {ornament}
        <span
          style={{
            fontFamily:    fontSerif,
            fontSize:      'clamp(30px, 3.2vw, 44px)',
            fontWeight:    400,
            letterSpacing: '0.28em',
            textTransform: 'uppercase',
            color:         brandColor,
            lineHeight:    1,
            paddingLeft:   '0.28em',
            opacity:       loaded ? 1 : 0,
            transform:     loaded ? 'translateY(0)' : 'translateY(6px)',
            transition:    'opacity 0.7s ease 0.05s, transform 0.7s cubic-bezier(0.22, 0.61, 0.36, 1) 0.05s, color 0.3s ease',
          }}
        >
          LWD
        </span>
        {ornament}
        <span style={ruleStyle('right')} />
      </div>

      {/* Tagline */}
      <span style={{
        fontFamily:    fontUtility,
        fontSize:      9,
        fontWeight:    500,
        letterSpacing: '0.42em',
        textTransform: 'uppercase',
        color:         gold,
        whiteSpace:    'nowrap',
        opacity:       loaded ? 1 : 0,
        transform:     loaded ? 'translateY(0)' : 'translateY(4px)',
        transition:    'opacity 0.6s ease 0.6s, transform 0.6s ease 0.6s',
        paddingLeft:   '0.42em',
      }}>
        Luxury Wedding Directory
      </span>
    </div>
  );
}

export default function HomeNav({ onToggleDark, darkMode, onVendorLogin, onNavigateStandard, onNavigateAbout, onNavigateAdvisory, hasHero = true, heroImg = null, hideMasthead = false, lightScheme = false, sticky = false }) {
  const C = useTheme();
  const [scrolled,       setScrolled]       = useState(false);
  const [drawerOpen,     setDrawerOpen]     = useState(false);
  const [navItems,       setNavItems]       = useState([]); // Start empty, use fallback only on error
  const [openPanel,      setOpenPanel]      = useState(null); // nav item id | null
  const [branding,       setBranding]       = useState(DEFAULT_BRANDING);
  const [keyboardFocus,  setKeyboardFocus]  = useState(null); // Track keyboard focus for arrow nav
  const [isMobile,       setIsMobile]       = useState(() => typeof window !== "undefined" ? window.innerWidth < 768 : false);
  const navRef        = useRef(null);
  const closeTimer    = useRef(null);
  const hamburgerRef  = useRef(null); // Track hamburger button for focus restoration
  const drawerRef     = useRef(null); // Track drawer for focus management

  // ── Two-state logic ──────────────────────────────────────────────────────────
  // transparent = on a hero page AND not yet scrolled past threshold
  // solid       = scrolled, OR no hero on this page
  const isTransparent = hasHero && !scrolled;

  // ── Adaptive colour, samples the hero image top strip ───────────────────────
  // isTransparent must be declared first (used as the activation gate).
  // Falls back to dark (safe for light text) while loading or if CORS blocks.
  const heroColor = useAdaptiveColor(isTransparent && heroImg ? heroImg : null, { region: 'top', sampleHeight: 80 });

  // ── Solid state colour tokens ─────────────────────────────────────────────
  // lightScheme=true → cream header (used on pages that own their own bg,
  // e.g. editorial-standards). lightScheme=false → dark cinematic (default).
  const solidBg     = lightScheme ? "rgba(250,248,244,0.98)" : "rgba(5,5,5,0.92)";
  const solidBrand  = lightScheme ? "#1a1410"                : "#ffffff";
  const solidLink   = lightScheme ? "rgba(26,20,16,0.70)"    : "rgba(255,255,255,0.88)";
  const solidIcon   = lightScheme ? "rgba(26,20,16,0.38)"    : "rgba(255,255,255,0.5)";
  const solidBorder = lightScheme ? "rgba(0,0,0,0.05)"       : "rgba(255,255,255,0.08)";
  const solidEdge   = lightScheme ? "1px solid rgba(211,154,33,0.22)" : "1px solid rgba(211,154,33,0.35)";
  const solidShadow = "none";

  // ── Ghost state (transparent, over hero, desktop only) ───────────────────────
  const heroIsDark   = heroColor.isDark || heroColor.isMidtone;
  const ghostOpacity = heroIsDark ? 0.035 : 0.025;
  const ghostBase    = heroIsDark ? "4,3,2" : "247,243,236";

  // Mobile: always dark cinematic bar. Desktop: adaptive ghost over hero.
  const navBg     = isTransparent ? (isMobile ? solidBg : `rgba(${ghostBase},${ghostOpacity})`) : solidBg;
  // Mobile always shows gold border; desktop hides it over the hero.
  const navBorder = (!isTransparent || isMobile) ? solidEdge : "none";
  const navShadow = "none";

  // Nav is always dark, text is always light regardless of hero brightness.
  const brandColor = solidBrand;
  const linkColor  = solidLink;
  const iconColor  = solidIcon;
  const iconBorder = solidBorder;

  const openMegaMenu = useCallback(id => {
    clearTimeout(closeTimer.current);
    setOpenPanel(id);
  }, []);

  const startClose = useCallback(() => {
    closeTimer.current = setTimeout(() => setOpenPanel(null), 120);
  }, []);

  const cancelClose = useCallback(() => {
    clearTimeout(closeTimer.current);
  }, []);

  // Keyboard navigation handler for desktop mega menus
  const handleMegaMenuKeyDown = useCallback((e, itemId, isMega) => {
    const nonCtaItems = navItems.filter(i => i.type !== "cta");
    const currentIndex = nonCtaItems.findIndex(i => i.id === itemId);

    if (isMega) {
      if (e.key === "Enter" || e.key === " ") {
        // Enter/Space: open mega menu
        e.preventDefault();
        openMegaMenu(itemId);
        setKeyboardFocus(itemId);
      } else if (e.key === "Escape") {
        // ESC: close mega menu
        e.preventDefault();
        setOpenPanel(null);
        setKeyboardFocus(itemId);
      } else if (e.key === "ArrowRight") {
        // Arrow Right: move to next item
        e.preventDefault();
        const nextIndex = (currentIndex + 1) % nonCtaItems.length;
        const nextId = nonCtaItems[nextIndex].id;
        setKeyboardFocus(nextId);
        // Find and focus the next button
        setTimeout(() => {
          document.querySelector(`[data-nav-id="${nextId}"]`)?.focus();
        }, 0);
      } else if (e.key === "ArrowLeft") {
        // Arrow Left: move to previous item
        e.preventDefault();
        const prevIndex = currentIndex === 0 ? nonCtaItems.length - 1 : currentIndex - 1;
        const prevId = nonCtaItems[prevIndex].id;
        setKeyboardFocus(prevId);
        // Find and focus the previous button
        setTimeout(() => {
          document.querySelector(`[data-nav-id="${prevId}"]`)?.focus();
        }, 0);
      }
    }
  }, [navItems, openMegaMenu]);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 80);
    window.addEventListener("scroll", handler, { passive: true });
    // Initialise immediately (page may already be scrolled on mount)
    handler();
    return () => window.removeEventListener("scroll", handler);
  }, []);

  useEffect(() => {
    if (!navRef.current) return;
    const write = () => {
      const h = navRef.current?.getBoundingClientRect().height ?? 64;
      document.documentElement.style.setProperty('--nav-h', `${h}px`);
    };
    write();
    const ro = new ResizeObserver(write);
    ro.observe(navRef.current);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", check, { passive: true });
    return () => window.removeEventListener("resize", check);
  }, []);

  // Lock body scroll when drawer is open
  useEffect(() => {
    document.body.style.overflow = drawerOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [drawerOpen]);

  // Focus management: trap focus inside drawer + restore on close
  useEffect(() => {
    if (!drawerOpen) return;

    // Store the element that triggered the drawer (hamburger button)
    const triggerElement = hamburgerRef.current;

    // Focus trap: prevent Tab from escaping drawer
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setDrawerOpen(false);
        triggerElement?.focus();
        return;
      }

      // Tab trap: keep focus within drawer
      if (e.key === "Tab" && drawerRef.current) {
        const focusableElements = drawerRef.current.querySelectorAll(
          "button, [href], input, select, textarea, [tabindex]:not([tabindex='-1'])"
        );
        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (e.shiftKey) {
          // Shift+Tab (reverse)
          if (document.activeElement === firstElement) {
            lastElement?.focus();
            e.preventDefault();
          }
        } else {
          // Tab forward
          if (document.activeElement === lastElement) {
            firstElement?.focus();
            e.preventDefault();
          }
        }
      }
    };

    // Focus the drawer on open (for accessibility)
    drawerRef.current?.focus();

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [drawerOpen]);

  // Restore focus when drawer closes
  useEffect(() => {
    if (drawerOpen) return;
    // Give animation time to complete before returning focus
    const timer = setTimeout(() => {
      hamburgerRef.current?.focus();
    }, 350);
    return () => clearTimeout(timer);
  }, [drawerOpen]);

  // Click outside handler: close mega menu when clicking outside nav
  useEffect(() => {
    if (!openPanel) return;

    const handleClickOutside = (e) => {
      // Don't close if clicking inside nav
      if (navRef.current && navRef.current.contains(e.target)) return;
      // Don't close if clicking inside the portalled panel (it renders outside navRef)
      const panelEl = document.getElementById(`mega-menu-${openPanel}`);
      if (panelEl && panelEl.contains(e.target)) return;
      // Close mega menu
      setOpenPanel(null);
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openPanel]);

  // Load site branding (logo + layout). The DB row may still hold the old
  // 'logo-left' layout and 'Luxury Wedding Directory' text from before the
  // identity refresh, force the new LWD-centred-stacked masthead so the
  // visual system stays consistent with /the-edit until the admin UI is
  // updated to match. Custom logo images and per-tenant tweaks still flow
  // through normally; only the layout + wordmark text are pinned.
  useEffect(() => {
    supabase
      .from("site_branding")
      .select("*")
      .eq("id", "main")
      .maybeSingle()
      .then(({ data }) => {
        const merged = data ? { ...DEFAULT_BRANDING, ...data } : DEFAULT_BRANDING;
        setBranding({
          ...merged,
          logo_text:     "LWD",
          header_layout: "logo-center-stacked",
          // The real imgUrl/HomeNav fix (explicit height on the resize:'contain'
          // transform, so the wide lockup doesn't squish) shipped and was
          // verified live 2026-09-13 — this now matches the site_branding DB
          // row (logo_type:'image', flipped the same day), so it no longer
          // needs to be pinned here independently of dev/prod. Kept as an
          // explicit override (not just a DB read) only for the two locked
          // logo asset URLs and the approved widths, in case the DB row drifts.
          logo_type: 'image',
          logo_image_light: 'https://qpkggfibwreznussudfh.supabase.co/storage/v1/object/public/brand-assets/logo/luxury-wedding-directory-serif-black.png',
          logo_image_dark:  'https://qpkggfibwreznussudfh.supabase.co/storage/v1/object/public/brand-assets/logo/luxury-wedding-directory-serif-white.png',
          // 232/155 = the approved +15% (207/138) with a further +12% on
          // top per Tai's 2026-09-12 note ("8 to 12 percent... deserves a
          // little more presence"), typography now locked.
          header_logo_width_desktop: 232,
          header_logo_width_mobile: 155,
        });
      });
  }, []);

  // Load nav items from Supabase, fall back to static list on error
  useEffect(() => {
    (async () => {
      try {
        const { data, error } = await supabase
          .from("nav_items")
          .select("*")
          .eq("section", "directory")
          .order("position", { ascending: true });

        if (error) {
          console.error("[HomeNav] Supabase error, using fallback:", error);
          setNavItems(FALLBACK_LINKS);
          return;
        }

        if (data && data.length > 0) {
          console.log("[HomeNav] Loaded", data.length, "nav items from database");
          // Filter only visible items and build tree structure
          const visibleItems = data
            .filter(i => i.visible)
            .filter(i => !isHomeLink(i))     // wordmark click already returns home
            .filter(i => !isHiddenLink(i))   // drop pages temporarily removed from public nav
            .map(translateLegacyCta);
          const tree = buildTree(visibleItems);
          setNavItems(injectVendorMega(tree));
        } else {
          console.warn("[HomeNav] No nav items in database, using fallback");
          setNavItems(FALLBACK_LINKS);
        }
      } catch (err) {
        console.error("[HomeNav] Exception loading nav, using fallback:", err);
        setNavItems(FALLBACK_LINKS);
      }
    })();
  }, []);

  const handlers = { onNavigateAbout, onNavigateStandard, onVendorLogin };

  // ── Layout flags ─────────────────────────────────────────────────────────────
  const isStacked  = branding.header_layout === "logo-center-stacked" || branding.header_layout === "logo-above-centered";
  const isSplit    = branding.header_layout === "split-center-logo";
  const isCenterMenu = branding.header_layout === "logo-left-center-menu";

  // Collapse only on fixed-position nav (hasHero pages), never on sticky
  // pages (shrinking a sticky nav's height on scroll causes a reflow-bounce,
  // see the masthead block below). Shared with the "H" home ornament so it
  // can swap for the wordmark at exactly the moment the masthead disappears,
  // rather than leaving the bare letter as the only surviving branding.
  const shouldCollapseMasthead = isStacked && scrolled && !isMobile && !sticky;

  // Resolve logo image src: the "light" (dark-text) variant is only correct when the
  // resolved nav background is actually light — that's ONLY the non-transparent state
  // on a lightScheme page (solidBg is dark, rgba(5,5,5,.92), by default otherwise).
  // Was previously `!isTransparent || !branding.transparent_bg_expected`, which treated
  // every non-transparent nav as light — wrong, since the default solid nav is dark.
  const logoImgSrc = (() => {
    if (branding.logo_type !== "image" && branding.logo_type !== "image+text") return null;
    const useLight = !isTransparent && lightScheme;
    const light = branding.logo_image_light;
    const dark  = branding.logo_image_dark;
    // Explicit height, matching the real 1830x488 cropped lockup's aspect
    // ratio — resize:'contain' with only a width silently leaves height at
    // the source's native pixels instead of scaling it, squishing a wide
    // logo into a near-square box (found 2026-09-12).
    // 500 (was 400) — headroom for the larger approved display width (228px)
    // at 2x retina density, so the logo stays crisp rather than upscaled.
    const LOGO_W = 500;
    const LOGO_H = Math.round(LOGO_W * (488 / 1830));
    if (useLight)  return imgUrl(light || dark, { width: LOGO_W, height: LOGO_H, resize: 'contain' }) || null;
    return imgUrl(dark || light, { width: LOGO_W, height: LOGO_H, resize: 'contain' }) || null;
  })();

  const logoWidth = window?.innerWidth < 768
    ? (branding.header_logo_width_mobile || 120)
    : (branding.header_logo_width_desktop || 180);

  // ── Logo renderer ─────────────────────────────────────────────────────────
  function renderLogo() {
    if (!branding.show_logo_in_header) return null;

    const handleClick = () => {
      const target = branding.logo_link_target || "/";
      const normalised = target === "" ? "/" : target;
      if (normalised === "/" && window.location.pathname === "/") {
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        navigateTo(normalised);
      }
    };

    const isImageType = branding.logo_type === "image" || branding.logo_type === "image+text" || branding.logo_type === "icon";
    const isTextType  = branding.logo_type === "text" || branding.logo_type === "image+text";

    return (
      <div
        style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", flexShrink: 0 }}
        onClick={handleClick}
      >
        {/* Image / icon */}
        {isImageType && logoImgSrc && (
          <img
            src={logoImgSrc}
            alt={branding.logo_alt_text || "Site logo"}
            style={{
              width: branding.header_logo_rendering === "fixed-width" ? logoWidth : "auto",
              height: branding.header_logo_rendering === "fixed-height" ? 40 : "auto",
              maxWidth: logoWidth,
              // 67 (was 52 pre-2026-09-12: +15%, then +12% more, both approved)
              // — must scale together with header_logo_width_desktop/mobile
              // below, or this becomes the binding constraint and silently
              // caps the width increase (logo aspect ratio is wide, ~3.75:1).
              maxHeight: 67,
              objectFit: branding.header_logo_rendering === "cover" ? "cover" : "contain",
              display: "block",
              marginTop: 4,
            }}
          />
        )}
        {/* Text, LWD masthead lockup or legacy text treatment */}
        {isTextType && branding.logo_text === "LWD" && (
          <LwdMasthead brandColor={branding.logo_color || brandColor} gold="#d39a21" fontSerif={branding.logo_font === "sans" ? NU : GD} fontUtility={NU} isMobile={isMobile} />
        )}
        {isTextType && branding.logo_text !== "LWD" && (
          <div
            className="home-nav-brand"
            style={{
              fontFamily:    branding.logo_font === "sans" ? NU : GD,
              fontSize:      20,
              fontWeight:    600,
              color:         branding.logo_color || brandColor,
              letterSpacing: 0.5,
              transition:    "color 0.3s ease",
            }}
          >
            {/* Preserve the gold "Wedding" highlight for the legacy default text */}
            {branding.logo_text === "Luxury Wedding Directory" && !branding.logo_color
              ? <>Luxury <span style={{ color: C.gold }}>Wedding</span> Directory</>
              : (branding.logo_text || "LWD")}
          </div>
        )}
      </div>
    );
  }

  // ── Shared nav links block (used in both layouts) ─────────────────────────
  function renderNavLinks() {
    // Read once per render, not once per nav item, this value is identical
    // for every item in the loop below, so calling getBoundingClientRect()
    // inside the .map() was a forced-reflow multiplier for no benefit.
    const navH = navRef.current ? navRef.current.getBoundingClientRect().bottom : 64;
    return (
      <div
        className="home-nav-right-desktop"
        style={{ display: "flex", gap: isStacked ? 24 : 'clamp(10px, 1.4vw, 24px)', alignItems: "center" }}
      >
        {navItems.filter(i => i.type !== "cta").map((item) => {
          const FONT_MAP = { serif: GD, mono: "'JetBrains Mono','Fira Mono',monospace", sans: NU };
          const labelFont = FONT_MAP[item.label_font] || NU;
          const itemColor = item.label_color || linkColor;
          const handler   = resolveHandler(item, handlers);
          const isMega    = item.type === "mega_menu" || item.type === "dropdown";

          if (isMega) {
            return (
              <div
                key={item.id}
                style={{ position: "relative" }}
                onMouseEnter={() => openMegaMenu(item.id)}
                onMouseLeave={startClose}
              >
                <button
                  data-nav-id={item.id}
                  className="home-nav-links"
                  onClick={handler || undefined}
                  onKeyDown={(e) => handleMegaMenuKeyDown(e, item.id, true)}
                  onFocus={() => setKeyboardFocus(item.id)}
                  onBlur={() => {
                    if (openPanel !== item.id) setKeyboardFocus(null);
                  }}
                  aria-haspopup="menu"
                  aria-expanded={openPanel === item.id}
                  aria-controls={`mega-menu-${item.id}`}
                  style={{
                    background: "none", border: "none",
                    cursor: "pointer", fontSize: 'clamp(11px, 0.9vw, 13px)', fontWeight: 300,
                    color: openPanel === item.id ? C.gold : linkColor,
                    fontFamily: NU,
                    letterSpacing: "0.18em",
                    textTransform: "uppercase",
                    transition: "color 0.2s",
                    display: "flex", alignItems: "center", gap: 4,
                  }}
                >
                  {item.label}
                  <span style={{ fontSize: 9, opacity: 0.6, transition: "transform 0.2s", transform: openPanel === item.id ? "rotate(180deg)" : "rotate(0deg)" }}>▾</span>
                </button>
                {openPanel === item.id && createPortal(
                  <MegaMenuPanel
                    id={`mega-menu-${item.id}`}
                    item={item}
                    navHeight={navH}
                    onMouseEnter={cancelClose}
                    onMouseLeave={startClose}
                  />,
                  document.body
                )}
              </div>
            );
          }

          // ── Special case: "H" masthead ornament ──────────────────────────
          // The single-letter Home affordance gets editorial treatment to
          // pull its weight in the masthead cluster:
          //   • serif (matches the LWD wordmark family) so it reads as a
          //     typographic mark, not a SaaS button label
          //   • slightly larger than the other nav items
          //   • hairline gold rule draws underneath on hover/focus, from
          //     the centre outward (the Vogue-masthead detail)
          //   • a tiny "HOME" caption in gold caps fades in directly below
          //     so first-time visitors and screen-reader users immediately
          //     know what it does, the whole point of the H per the brief
          //   • aria-label + title so accessible-tech announces "Home", not
          //     a stray letter
          //
          // Keyboard focus mirrors hover so tabbing through the nav reveals
          // the caption too, H reads great either way you reach it.
          const isHomeOrnament = item.nav_action === "home" || item.label === "H";
          if (isHomeOrnament) {
            const reveal = (e) => {
              e.currentTarget.style.color = C.gold;
              const rule = e.currentTarget.querySelector('[data-h-rule]');
              const cap  = e.currentTarget.querySelector('[data-h-cap]');
              if (rule) rule.style.transform = "scaleX(1)";
              if (cap)  { cap.style.opacity = "1"; cap.style.transform = "translate(-50%, 0)"; }
            };
            const conceal = (e) => {
              e.currentTarget.style.color = linkColor;
              const rule = e.currentTarget.querySelector('[data-h-rule]');
              const cap  = e.currentTarget.querySelector('[data-h-cap]');
              if (rule) rule.style.transform = "scaleX(0)";
              if (cap)  { cap.style.opacity = "0"; cap.style.transform = "translate(-50%, -3px)"; }
            };
            return (
              <button
                key={item.id}
                className="home-nav-links home-nav-h"
                onClick={handler || undefined}
                aria-label="Home"
                title="Home"
                onMouseEnter={reveal}
                onMouseLeave={conceal}
                onFocus={reveal}
                onBlur={conceal}
                style={{
                  background:    "none",
                  border:        "none",
                  cursor:        handler ? "pointer" : "default",
                  position:      "relative",
                  padding:       "4px 6px 6px",
                  margin:        "0 4px 0 0",
                  fontFamily:    GD,
                  fontSize:      'clamp(15px, 1.25vw, 19px)',
                  fontWeight:    400,
                  color:         linkColor,
                  letterSpacing: "0.04em",
                  lineHeight:    1,
                  transition:    "color 0.25s ease",
                  display:       "inline-flex",
                  alignItems:    "center",
                  justifyContent:"center",
                }}
              >
                <span style={{ position: "relative", display: "inline-flex", alignItems: "center", padding: "0 1px" }}>
                  {/* Once the masthead collapses on scroll, its wordmark is
                      the only branding visible on the page — swap the bare
                      "H" for a small version of the same logo asset rather
                      than leaving a single stray letter to carry the brand. */}
                  {shouldCollapseMasthead && logoImgSrc ? (
                    <img
                      src={logoImgSrc}
                      alt="Luxury Wedding Directory"
                      style={{ height: 18, width: "auto", maxWidth: 110, objectFit: "contain", display: "block" }}
                    />
                  ) : "H"}
                  {/* Hairline gold rule that draws from centre outward on
                      hover/focus, the Vogue-masthead detail. */}
                  <span
                    aria-hidden
                    data-h-rule
                    style={{
                      position:         "absolute",
                      left:             -2,
                      right:            -2,
                      bottom:           -4,
                      height:           1,
                      background:       C.gold,
                      transform:        "scaleX(0)",
                      transformOrigin:  "center",
                      transition:       "transform 0.45s cubic-bezier(0.22, 0.61, 0.36, 1)",
                    }}
                  />
                </span>
                {/* "HOME" caption, fades in below on hover/focus. Sits
                    absolutely so it never expands the nav layout, only
                    appears when the visitor is actually engaging with
                    the H. Tiny gold caps, magazine vocabulary. */}
                <span
                  aria-hidden
                  data-h-cap
                  style={{
                    position:       "absolute",
                    top:            "100%",
                    left:           "50%",
                    transform:      "translate(-50%, -3px)",
                    marginTop:      4,
                    fontFamily:     NU,
                    fontSize:       8,
                    fontWeight:     600,
                    letterSpacing:  "0.32em",
                    textTransform:  "uppercase",
                    color:          C.gold,
                    whiteSpace:     "nowrap",
                    opacity:        0,
                    transition:     "opacity 0.28s ease, transform 0.32s cubic-bezier(0.22, 0.61, 0.36, 1)",
                    pointerEvents:  "none",
                  }}
                >
                  Home
                </span>
              </button>
            );
          }

          return (
            <button
              key={item.id}
              className="home-nav-links"
              onClick={handler || undefined}
              style={{
                background: "none", border: "none",
                cursor: handler ? "pointer" : "default",
                fontSize: 'clamp(11px, 0.9vw, 13px)', fontWeight: 300, color: linkColor,
                fontFamily: NU,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                transition: "color 0.2s",
              }}
              onMouseEnter={e => (e.currentTarget.style.color = C.gold)}
              onMouseLeave={e => (e.currentTarget.style.color = linkColor)}
            >
              {item.label}
            </button>
          );
        })}

        {/* Advisory quiet link, sits to the left of the divider + Apply CTA.
            Same font/weight as nav items so it reads as part of the menu,
            not as a second CTA competing with Apply for Inclusion. */}
        {onNavigateAdvisory && (
          <button
            onClick={onNavigateAdvisory}
            style={{
              background: "none", border: "none", cursor: "pointer", padding: 0,
              fontFamily: NU,
              fontSize: 'clamp(11px, 0.9vw, 13px)',
              fontWeight: 300,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: C.nav,
              marginLeft: 'clamp(10px, 1.4vw, 20px)',
              whiteSpace: 'nowrap',
              transition: 'color 0.2s',
            }}
            onMouseEnter={e => e.currentTarget.style.color = C.gold}
            onMouseLeave={e => e.currentTarget.style.color = C.nav}
          >
            Advisory
          </button>
        )}

        {/* Editorial CTA cluster, quiet gold text link with diamond ornament,
            preceded by a hairline gold divider that separates it from the
            menu items. Replaces the old solid-pill button so the right-side
            action joins the masthead's restraint instead of fighting it.
            The lights toggle that used to live here has moved to /the-edit
            only, the directory side stays committed to a single look. */}
        {(() => {
          const ctaItems = navItems.filter(i => i.type === "cta");
          if (ctaItems.length === 0) return null;
          return (
            <>
              <span aria-hidden style={{
                width: 1, height: 18,
                background: '#d39a21',
                // Extra space before the divider so the CTA reads as a
                // final decision point, well separated from the menu cluster.
                marginLeft:  'clamp(20px, 3.4vw, 48px)',
                marginRight: 'clamp(8px, 1.2vw, 18px)',
                flexShrink: 0,
              }} />
              {ctaItems.map(item => {
                const handler = resolveHandler(item, handlers);
                return (
                  <button
                    key={item.id}
                    onClick={handler || undefined}
                    style={{
                      background:    'none',
                      border:        'none',
                      outline:       'none',
                      cursor:        'pointer',
                      padding:       0,
                      fontFamily:    NU,
                      // Matched to the other nav items so the CTA reads as
                      // part of the same masthead family, same size, same
                      // weight, same caps tracking.
                      fontSize:      'clamp(11px, 0.9vw, 13px)',
                      // Master logo gold (#d39a21, the same gold as the LWD
                      // masthead lockup), a touch heavier than the menu items
                      // with a soft shadow so the one action stays legible over
                      // any hero slide without shouting.
                      fontWeight:    400,
                      letterSpacing: '0.18em',
                      textTransform: 'uppercase',
                      color:         '#d39a21',
                      textShadow:    '0 1px 10px rgba(0,0,0,0.5)',
                      display:       'inline-flex',
                      alignItems:    'center',
                      gap:           10,
                      whiteSpace:    'nowrap',
                      transition:    'opacity 0.2s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.opacity = '0.7'}
                    onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                  >
                    {item.label}
                    {/* Diamond ornament, same vocabulary as the LWD masthead
                        lockup, so the CTA reads as part of the same family. */}
                    <span aria-hidden style={{
                      width: 5, height: 5,
                      background: '#d39a21',
                      transform: 'rotate(45deg)',
                      flexShrink: 0,
                    }} />
                  </button>
                );
              })}
            </>
          );
        })()}

        {/* Lights toggle, quiet dim glyph, sized 25% bigger than the
            previous 10px so it has a touch more presence (now 13px) while
            still reading as trim, not a UI control. Softer alpha (~60%
            gold) keeps it from pulling the eye. */}
        {onToggleDark && (
          <button
            onClick={onToggleDark}
            title={darkMode ? "Lights off (dark)" : "Lights on (light)"}
            aria-label={darkMode ? "Switch to light mode" : "Switch to dark mode"}
            style={{
              fontFamily:  NU,
              fontSize:    13,
              lineHeight:  1,
              color:       '#d39a21',
              background:  'none',
              border:      'none',
              outline:     'none',
              cursor:      'pointer',
              padding:     0,
              marginLeft:  'clamp(10px, 1.6vw, 22px)',
              display:     'inline-flex',
              alignItems:  'center',
              flexShrink:  0,
              transition:  'opacity 0.2s, color 0.2s',
            }}
            onMouseEnter={e => { e.currentTarget.style.color = C.gold; }}
            onMouseLeave={e => { e.currentTarget.style.color = '#d39a21'; }}
          >
            {darkMode ? '☀' : '☾'}
          </button>
        )}
      </div>
    );
  }

  return (
    <>
      <nav
        ref={navRef}
        aria-label="Primary navigation"
        role="navigation"
        className="home-nav"
        style={{
          position: isMobile ? "relative" : (sticky ? "sticky" : "fixed"),
          top: 0,
          left: 0,
          right: 0,
          zIndex: 700,
          // Padding removed from the outer wrapper for stacked layout,
          // each inner section (masthead / menu row) owns its own padding.
          // Non-stacked layouts keep the dynamic padding on the wrapper.
          padding: isStacked ? 0 : (isTransparent ? "20px clamp(16px, 3vw, 40px)" : "12px clamp(16px, 3vw, 40px)"),
          background: navBg,
          backdropFilter:       isMobile ? "blur(12px)" : isTransparent ? "blur(4px)" : "blur(14px) saturate(1.08)",
          WebkitBackdropFilter: isMobile ? "blur(12px)" : isTransparent ? "blur(4px)" : "blur(14px) saturate(1.08)",
          borderBottom: navBorder,
          boxShadow: navShadow,
          display: "flex",
          flexDirection: isStacked ? "column" : "row",
          alignItems: isStacked ? "stretch" : "center",
          justifyContent: isStacked ? undefined : ((isSplit || isCenterMenu) ? "center" : "space-between"),
          transition: "background 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease",
          overflowX: "hidden",
        }}
      >
        {/* ── Layout: logo-left (standard) ── */}
        {!isStacked && !isSplit && !isCenterMenu && (
          <>
            {renderLogo()}
            {renderNavLinks()}
          </>
        )}

        {/* ── Layout: logo-center-stacked / logo-above-centered ──
            Masthead (LWD wordmark + rules + tagline) sits in its own row
            and collapses on scroll, it is the brand reveal, not the nav.
            The menu row below it is the only thing that stays sticky, so
            once the masthead is gone there is no duplicated logo. */}
        {isStacked && (
          <>
            {/* Masthead, visible on page load, slides away on scroll.
                hideMasthead=true suppresses it entirely.
                On sticky nav pages (no hero, e.g. /shop) the collapse is
                intentionally disabled: the nav is in layout flow, so
                shrinking its height on scroll causes a reflow-bounce.
                On fixed-position hero pages (home) the collapse is safe
                because fixed elements never interact with document flow. */}
            {!hideMasthead && (() => {
              const shouldCollapse = shouldCollapseMasthead;
              return (
              <div style={{
                overflow: "hidden",
                position: "relative",
                willChange: "max-height, opacity",
                maxHeight: shouldCollapse ? "0px" : "200px",
                opacity: shouldCollapse ? 0 : 1,
                padding: shouldCollapse ? 0 : (isMobile ? "7px clamp(16px, 3vw, 40px) 7px" : "14px clamp(16px, 3vw, 40px) 15px"),
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                transition: "max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.22s ease, padding 0.3s ease",
                pointerEvents: shouldCollapse ? "none" : "auto",
              }}>
              {renderLogo()}
              {/* Mobile hamburger, lives here so it sits inline with the
                  centred logo in a single row, not in the empty menu row below. */}
              {isMobile && (
                <button
                  ref={hamburgerRef}
                  className="home-nav-hamburger"
                  onClick={() => setDrawerOpen(true)}
                  aria-label="Open navigation menu"
                  aria-expanded={drawerOpen}
                  aria-controls="home-nav-drawer"
                  style={{
                    display: "flex",
                    position: "absolute",
                    right: "clamp(16px, 3vw, 40px)",
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: "8px 6px",
                    flexDirection: "column",
                    gap: 5,
                    minWidth: 44,
                    minHeight: 44,
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <span style={{ width: 22, height: 1.5, background: brandColor, borderRadius: 1, display: "block" }} />
                  <span style={{ width: 16, height: 1.5, background: C.gold, borderRadius: 1, display: "block", marginLeft: "auto" }} />
                  <span style={{ width: 22, height: 1.5, background: brandColor, borderRadius: 1, display: "block" }} />
                </button>
              )}
            </div>);
            })()}

            {/* Menu row, the only visible element once scrolled.
                No background of its own: the outer nav carries the glass
                treatment so the two sections share one blur surface and
                avoid a visible seam between them.
                Hidden on mobile while the masthead is open (hamburger lives
                in the masthead row on mobile). Reappears when scrolled so
                the hamburger remains accessible after the masthead collapses. */}
            <div style={{
              display: isMobile && !scrolled ? "none" : "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "10px clamp(16px, 3vw, 40px)",
              position: "relative",
              minHeight: 60,
            }}>
              {renderNavLinks()}
              {/* Hamburger (mobile only), lives inside the menu row so it
                  remains at the correct edge when the masthead is hidden. */}
              <button
                ref={hamburgerRef}
                className="home-nav-hamburger"
                onClick={() => setDrawerOpen(true)}
                aria-label="Open navigation menu"
                aria-expanded={drawerOpen}
                aria-controls="home-nav-drawer"
                style={{
                  display: "none",
                  position: "absolute",
                  right: "clamp(16px, 3vw, 40px)",
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: "8px 6px",
                  flexDirection: "column",
                  gap: 5,
                  minWidth: 44,
                  minHeight: 44,
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <span style={{ width: 22, height: 1.5, background: brandColor, borderRadius: 1, display: "block" }} />
                <span style={{ width: 16, height: 1.5, background: C.gold, borderRadius: 1, display: "block", marginLeft: "auto" }} />
                <span style={{ width: 22, height: 1.5, background: brandColor, borderRadius: 1, display: "block" }} />
              </button>
            </div>
          </>
        )}

        {/* ── Layout: split-center-logo (nav split left+right, logo center) ── */}
        {isSplit && (() => {
          const nonCtaItems = navItems.filter(i => i.type !== "cta");
          const ctaItems    = navItems.filter(i => i.type === "cta");
          const half        = Math.ceil(nonCtaItems.length / 2);
          const leftItems   = nonCtaItems.slice(0, half);
          const rightItems  = nonCtaItems.slice(half);
          return (
            <div style={{ display: "flex", alignItems: "center", width: "100%", gap: 20 }}>
              {/* Left nav group */}
              <div style={{ display: "flex", gap: 24, alignItems: "center", flex: 1, justifyContent: "flex-end" }}>
                {leftItems.map(item => {
                  const handler = resolveHandler(item, handlers);
                  return (
                    <button key={item.id} className="home-nav-links" onClick={handler || undefined}
                      style={{ background: "none", border: "none", cursor: handler ? "pointer" : "default", fontSize: 13, fontWeight: 400, color: linkColor, fontFamily: NU, letterSpacing: "0.3px", transition: "color 0.2s" }}
                      onMouseEnter={e => (e.currentTarget.style.color = C.gold)}
                      onMouseLeave={e => (e.currentTarget.style.color = linkColor)}
                    >{item.label}</button>
                  );
                })}
              </div>
              {/* Center logo */}
              <div style={{ flexShrink: 0 }}>{renderLogo()}</div>
              {/* Right nav group + CTAs + toggle */}
              <div style={{ display: "flex", gap: 24, alignItems: "center", flex: 1 }}>
                {rightItems.map(item => {
                  const handler = resolveHandler(item, handlers);
                  return (
                    <button key={item.id} className="home-nav-links" onClick={handler || undefined}
                      style={{ background: "none", border: "none", cursor: handler ? "pointer" : "default", fontSize: 13, fontWeight: 400, color: linkColor, fontFamily: NU, letterSpacing: "0.3px", transition: "color 0.2s" }}
                      onMouseEnter={e => (e.currentTarget.style.color = C.gold)}
                      onMouseLeave={e => (e.currentTarget.style.color = linkColor)}
                    >{item.label}</button>
                  );
                })}
                {ctaItems.map(item => {
                  const handler = resolveHandler(item, handlers);
                  const isOutline = item.cta_style === "outline";
                  const isDark = item.cta_style === "dark";
                  return (
                    <button key={item.id} onClick={handler || undefined} style={{ background: isDark ? "#0a0906" : isOutline ? "transparent" : C.gold, color: isDark ? C.gold : isOutline ? C.gold : "#0a0906", border: `1px solid ${isDark ? "#333" : C.gold}`, borderRadius: "var(--lwd-radius-input)", padding: "8px 20px", fontSize: 10, fontWeight: 700, letterSpacing: "1.5px", textTransform: "uppercase", cursor: "pointer", fontFamily: NU, transition: "all 0.25s ease" }}
                      onMouseEnter={e => { e.currentTarget.style.background = C.gold; e.currentTarget.style.color = "#0a0906"; }}
                      onMouseLeave={e => { e.currentTarget.style.background = isDark ? "#0a0906" : isOutline ? "transparent" : C.gold; e.currentTarget.style.color = isDark ? C.gold : isOutline ? C.gold : "#0a0906"; }}
                    >{item.label}</button>
                  );
                })}
                {onToggleDark && (<>
                <div className="home-nav-divider" style={{ width: 1, height: 18, background: C.border2 }} />
                <button className="home-nav-dark-toggle" onClick={onToggleDark} aria-label={darkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
                  style={{ background: "none", border: `1px solid ${iconBorder}`, borderRadius: "var(--lwd-radius-input)", color: iconColor, width: 36, height: 36, fontSize: 15, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.3s ease" }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = C.gold; e.currentTarget.style.color = C.gold; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = iconBorder; e.currentTarget.style.color = iconColor; }}
                >{darkMode ? "☀" : "☽"}</button>
                </>)}
              </div>
            </div>
          );
        })()}

        {/* ── Layout: logo-left-center-menu (logo left, links centered) ── */}
        {isCenterMenu && (
          <div style={{ display: "flex", alignItems: "center", width: "100%", position: "relative" }}>
            {renderLogo()}
            <div style={{ position: "absolute", left: "50%", transform: "translateX(-50%)" }}>
              {renderNavLinks()}
            </div>
          </div>
        )}

        {/* Hamburger for non-stacked layouts (stacked layout has its own
            inside the menu row, absolutely positioned at the right edge) */}
        {!isStacked && (
          <button
            ref={hamburgerRef}
            className="home-nav-hamburger"
            onClick={() => setDrawerOpen(true)}
            aria-label="Open navigation menu"
            aria-expanded={drawerOpen}
            aria-controls="home-nav-drawer"
            style={{
              display: "none",
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: "8px 6px",
              flexDirection: "column",
              gap: 5,
              minWidth: 44,
              minHeight: 44,
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <span style={{ width: 22, height: 1.5, background: brandColor, borderRadius: 1, display: "block" }} />
            <span style={{ width: 16, height: 1.5, background: C.gold, borderRadius: 1, display: "block", marginLeft: "auto" }} />
            <span style={{ width: 22, height: 1.5, background: brandColor, borderRadius: 1, display: "block" }} />
          </button>
        )}
      </nav>

      {/* ── Mobile Side Drawer, rendered via portal so it escapes any
           ancestor transform/stacking context (e.g. showcase page wrapper) ── */}
      {createPortal(
        <>
          {/* Backdrop */}
          <div
            className="home-nav-drawer-backdrop"
            onClick={() => setDrawerOpen(false)}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 1200,
              background: "rgba(0,0,0,0.6)",
              backdropFilter: "blur(4px)",
              opacity: drawerOpen ? 1 : 0,
              pointerEvents: drawerOpen ? "auto" : "none",
              transition: "opacity 0.3s ease",
            }}
          />
          {/* Drawer panel */}
          <div
            ref={drawerRef}
            id="home-nav-drawer"
            role="dialog"
            aria-modal="true"
            aria-label="Navigation menu"
            className="home-nav-drawer"
            style={{
              position: "fixed",
              top: 0,
              right: 0,
              bottom: 0,
              zIndex: 1201,
              width: "clamp(280px, 85vw, 360px)",
              background: darkMode ? "rgba(12,11,8,0.98)" : "rgba(8,7,5,0.98)",
              backdropFilter: "blur(24px)",
              transform: drawerOpen ? "translateX(0)" : "translateX(100%)",
              transition: "transform 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
              display: "flex",
              flexDirection: "column",
              padding: "0 0 clamp(20px, 5vw, 40px)",
              overflowY: "auto",
              outline: "none",
              tabIndex: -1,
            }}
          >
            {/* Drawer header */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "20px 24px", borderBottom: "1px solid rgba(211,154,33,0.15)" }}>
              <span style={{ fontFamily: GD, fontSize: 16, color: "#f5f0e8", fontWeight: 600 }}>
                Menu
              </span>
              <button
                onClick={() => setDrawerOpen(false)}
                aria-label="Close menu"
                style={{
                  background: "none",
                  border: "none",
                  color: "rgba(245,240,232,0.5)",
                  fontSize: 22,
                  cursor: "pointer",
                  padding: 4,
                  lineHeight: 1,
                  minWidth: 44,
                  minHeight: 44,
                }}
              >
                ×
              </button>
            </div>

            {/* Mobile Accordion Drawer, preserves hierarchy, handles mega menus */}
            <MobileDrawerAccordion
              navItems={navItems}
              onClose={() => setDrawerOpen(false)}
              darkMode={darkMode}
              onToggleDark={onToggleDark}
              handlers={handlers}
            />
          </div>
        </>,
        document.body
      )}
    </>
  );
}
